import {
  canBlockFloat,
  FLOAT_RESTORE_POSITION_PROP,
  type GroupDirection,
  getBlockFloat,
  getBlockFloatRestorePosition,
  isBlockFloated,
} from '@/lib/blocks/layout/group-layout';
import { normalizeBlocksStructure } from '@/lib/blocks/normalization';
import { sortBlocksByPosition } from '@/lib/blocks/tree';
import type { PageBlock } from '@/types/block';

export function hasFloatLayoutProps(props: Record<string, unknown>): boolean {
  return 'float' in props || FLOAT_RESTORE_POSITION_PROP in props;
}

function getSiblingIdsByParent(blocks: PageBlock[]): Map<string | null, string[]> {
  const siblingsByParent = new Map<string | null, PageBlock[]>();
  for (const block of blocks) {
    const parentId = block.parentBlockId ?? null;
    const siblings = siblingsByParent.get(parentId) ?? [];
    siblings.push(block);
    siblingsByParent.set(parentId, siblings);
  }

  return new Map(
    Array.from(siblingsByParent, ([parentId, siblings]) => [
      parentId,
      sortBlocksByPosition(siblings).map((block) => block.id),
    ]),
  );
}

/**
 * A saved restore slot is only meaningful while that parent's child order is unchanged. Explicit
 * inserts, deletes, and reorders establish a new layout, so affected floats keep their current
 * position instead of jumping to a stale slot when Wrap is later set to None.
 */
export function invalidateFloatRestorePositionsForChangedSiblings(
  beforeBlocks: PageBlock[],
  afterBlocks: PageBlock[],
): PageBlock[] {
  const beforeIdsByParent = getSiblingIdsByParent(beforeBlocks);
  const afterIdsByParent = getSiblingIdsByParent(afterBlocks);
  const parentIds = new Set([...beforeIdsByParent.keys(), ...afterIdsByParent.keys()]);
  const changedParentIds = new Set<string | null>();

  for (const parentId of parentIds) {
    if (
      !equalStringArrays(
        beforeIdsByParent.get(parentId) ?? [],
        afterIdsByParent.get(parentId) ?? [],
      )
    ) {
      changedParentIds.add(parentId);
    }
  }

  if (changedParentIds.size === 0) {
    return afterBlocks;
  }

  let didChange = false;
  const nextBlocks = afterBlocks.map((block) => {
    if (
      !changedParentIds.has(block.parentBlockId ?? null) ||
      !(FLOAT_RESTORE_POSITION_PROP in (block.props ?? {}))
    ) {
      return block;
    }

    const props = { ...(block.props ?? {}) };
    delete props[FLOAT_RESTORE_POSITION_PROP];
    didChange = true;
    return { ...block, props };
  });

  return didChange ? nextBlocks : afterBlocks;
}

function equalStringArrays(left: string[], right: string[]): boolean {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function applySiblingOrder(blocks: PageBlock[], orderedSiblings: PageBlock[]): PageBlock[] {
  const positions = new Map(orderedSiblings.map((block, index) => [block.id, index + 1] as const));
  return blocks.map((block) => {
    const position = positions.get(block.id);
    return position === undefined ? block : { ...block, position };
  });
}

function getSortedSiblings(blocks: PageBlock[], target: PageBlock): PageBlock[] {
  return sortBlocksByPosition(
    blocks.filter((block) => (block.parentBlockId ?? null) === (target.parentBlockId ?? null)),
  );
}

/** Reconstructs the sibling order before known floats were moved to the front. */
function getLogicalSiblingOrder(siblings: PageBlock[]): PageBlock[] {
  const knownFloats = siblings.filter(
    (block) => isBlockFloated(block) && getBlockFloatRestorePosition(block) !== null,
  );
  const slotCount =
    siblings.length -
    siblings.filter(
      (block) => isBlockFloated(block) && getBlockFloatRestorePosition(block) === null,
    ).length;
  const logicalSlots: Array<PageBlock | undefined> = new Array(slotCount);
  const placedIds = new Set<string>();

  for (const block of knownFloats) {
    const restorePosition = getBlockFloatRestorePosition(block);
    if (restorePosition === null) continue;
    const index = Math.min(slotCount - 1, restorePosition - 1);
    if (index >= 0 && logicalSlots[index] === undefined) {
      logicalSlots[index] = block;
      placedIds.add(block.id);
    }
  }

  const remaining = siblings.filter(
    (block) => !isBlockFloated(block) || (knownFloats.includes(block) && !placedIds.has(block.id)),
  );
  let remainingIndex = 0;
  for (let index = 0; index < logicalSlots.length; index += 1) {
    if (logicalSlots[index] === undefined) {
      logicalSlots[index] = remaining[remainingIndex];
      remainingIndex += 1;
    }
  }

  return logicalSlots.filter((block): block is PageBlock => block !== undefined);
}

/**
 * Applies props plus the document-order changes required by CSS float. Newly floated children must
 * precede the content that wraps around them, so their previous sibling position is retained in
 * props and restored when Wrap is set back to None.
 */
export function applyBlockPropsWithFloatLayout({
  blocks,
  blockId,
  props,
  rootDirection,
}: {
  blocks: PageBlock[];
  blockId: string;
  props: Record<string, unknown>;
  rootDirection: GroupDirection;
}): PageBlock[] {
  const target = blocks.find((block) => block.id === blockId);
  if (!target) {
    return blocks;
  }

  const parent = target.parentBlockId
    ? blocks.find((block) => block.id === target.parentBlockId)
    : undefined;
  const canFloatInParent = parent?.blockType === 'group' && canBlockFloat(target);
  const previousFloat = canFloatInParent ? getBlockFloat(target) : null;
  const previousRestorePosition = getBlockFloatRestorePosition(target);
  const siblings = getSortedSiblings(blocks, target);
  const logicalSiblings = getLogicalSiblingOrder(siblings);
  const nextProps = { ...props };
  const requestedFloat = getBlockFloat({ props: nextProps });
  const nextFloat = canFloatInParent ? requestedFloat : null;

  if (nextFloat) {
    nextProps.float = nextFloat;

    const requestedRestorePosition = getBlockFloatRestorePosition({ props: nextProps });
    const restorePosition =
      previousFloat === null
        ? (requestedRestorePosition ??
          logicalSiblings.findIndex((block) => block.id === target.id) + 1)
        : (previousRestorePosition ?? requestedRestorePosition);
    if (restorePosition !== null && restorePosition >= 1) {
      nextProps[FLOAT_RESTORE_POSITION_PROP] = restorePosition;
    } else {
      delete nextProps[FLOAT_RESTORE_POSITION_PROP];
    }
  } else {
    delete nextProps.float;
    delete nextProps[FLOAT_RESTORE_POSITION_PROP];
  }

  let nextBlocks = blocks.map((block) =>
    block.id === blockId ? { ...block, props: nextProps } : block,
  );

  if (
    (previousFloat === null && nextFloat !== null) ||
    (previousFloat !== null && nextFloat === null && previousRestorePosition !== null)
  ) {
    const nextTarget = nextBlocks.find((block) => block.id === blockId);
    if (nextTarget) {
      const nextSiblings = getSortedSiblings(nextBlocks, nextTarget);
      const activeFloats = nextSiblings.filter(isBlockFloated);
      const orderedFloats =
        previousFloat === null && nextFloat !== null
          ? [nextTarget, ...activeFloats.filter((block) => block.id !== blockId)]
          : activeFloats;
      const activeFloatIds = new Set(orderedFloats.map((block) => block.id));
      const flowSiblings = logicalSiblings
        .map((logicalBlock) => nextSiblings.find((nextBlock) => nextBlock.id === logicalBlock.id))
        .filter(
          (block): block is PageBlock => block !== undefined && !activeFloatIds.has(block.id),
        );
      nextBlocks = applySiblingOrder(nextBlocks, [...orderedFloats, ...flowSiblings]);
    }
  }

  return previousFloat === nextFloat
    ? nextBlocks
    : normalizeBlocksStructure(nextBlocks, rootDirection);
}
